import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';

interface GlobeProps {
  className?: string;
  size?: number;
  rotationSpeed?: number;
  glowIntensity?: number;
}

const GlobeContainer = styled.div<{ size: number }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  position: relative;
  margin: 0 auto;
`;

const Canvas = styled.canvas`
  border-radius: 50%;
  box-shadow: 
    0 0 20px ${theme.colors.primary[300]}40,
    inset 0 0 20px ${theme.colors.primary[200]}20;
`;

interface Point {
  x: number;
  y: number;
  z: number;
  originalX: number;
  originalY: number;
  originalZ: number;
  intensity: number;
}

export const Globe: React.FC<GlobeProps> = ({
  className,
  size = 200,
  rotationSpeed = 0.01,
  glowIntensity = 0.8
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationIdRef = useRef<number | undefined>(undefined);
  const pointsRef = useRef<Point[]>([]);
  const rotationRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = size;
    canvas.height = size;

    const radius = size / 2 - 20;
    const centerX = size / 2;
    const centerY = size / 2;

    // Create globe points
    const createPoints = () => {
      pointsRef.current = [];
      const pointCount = 800;
      
      for (let i = 0; i < pointCount; i++) {
        // Fibonacci sphere distribution for even point distribution
        const y = 1 - (i / (pointCount - 1)) * 2;
        const radiusAtY = Math.sqrt(1 - y * y);
        const theta = Math.PI * (3 - Math.sqrt(5)) * i;
        
        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;
        
        pointsRef.current.push({
          x: x * radius,
          y: y * radius,
          z: z * radius,
          originalX: x * radius,
          originalY: y * radius,
          originalZ: z * radius,
          intensity: Math.random() * 0.8 + 0.2
        });
      }
    };

    const rotatePoint = (point: Point, rotation: number) => {
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      
      point.x = point.originalX * cos - point.originalZ * sin;
      point.z = point.originalX * sin + point.originalZ * cos;
    };

    const projectPoint = (point: Point) => {
      const scale = 200 / (200 + point.z);
      return {
        x: centerX + point.x * scale,
        y: centerY + point.y * scale,
        scale: scale,
        visible: point.z > -radius * 0.8
      };
    };

    const drawGlobe = () => {
      ctx.clearRect(0, 0, size, size);
      
      // Draw background circle
      ctx.save();
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius + 10, 0, Math.PI * 2);
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius + 10);
      gradient.addColorStop(0, theme.colors.primary[900] + '20');
      gradient.addColorStop(0.7, theme.colors.primary[700] + '40');
      gradient.addColorStop(1, theme.colors.primary[500] + '60');
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.restore();

      // Sort points by z-depth for proper rendering
      const sortedPoints = pointsRef.current
        .map(point => ({ ...point, projected: projectPoint(point) }))
        .filter(p => p.projected.visible)
        .sort((a, b) => a.z - b.z);

      // Draw points
      sortedPoints.forEach(point => {
        const projected = point.projected;
        const alpha = Math.max(0.1, projected.scale * point.intensity * glowIntensity);
        const pointSize = Math.max(0.5, projected.scale * 2);
        
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = theme.colors.primary[400];
        ctx.shadowColor = theme.colors.primary[300];
        ctx.shadowBlur = pointSize * 2;
        ctx.beginPath();
        ctx.arc(projected.x, projected.y, pointSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      // Draw some connection lines between nearby points
      if (sortedPoints.length > 0) {
        ctx.save();
        ctx.strokeStyle = theme.colors.primary[400] + '40';
        ctx.lineWidth = 0.5;
        
        for (let i = 0; i < Math.min(sortedPoints.length, 50); i++) {
          const point1 = sortedPoints[i];
          for (let j = i + 1; j < Math.min(sortedPoints.length, 50); j++) {
            const point2 = sortedPoints[j];
            const distance = Math.sqrt(
              Math.pow(point1.originalX - point2.originalX, 2) +
              Math.pow(point1.originalY - point2.originalY, 2) +
              Math.pow(point1.originalZ - point2.originalZ, 2)
            );
            
            if (distance < radius * 0.3 && Math.random() > 0.95) {
              ctx.globalAlpha = 0.2;
              ctx.beginPath();
              ctx.moveTo(point1.projected.x, point1.projected.y);
              ctx.lineTo(point2.projected.x, point2.projected.y);
              ctx.stroke();
            }
          }
        }
        ctx.restore();
      }
    };

    const animate = () => {
      rotationRef.current += rotationSpeed;
      
      pointsRef.current.forEach(point => {
        rotatePoint(point, rotationSpeed);
      });
      
      drawGlobe();
      animationIdRef.current = requestAnimationFrame(animate);
    };

    createPoints();
    animate();

    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [size, rotationSpeed, glowIntensity]);

  return (
    <GlobeContainer className={className} size={size}>
      <Canvas ref={canvasRef} />
    </GlobeContainer>
  );
};

export default Globe;